import React, { useCallback, useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Theme,
  SxProps,
  useTheme,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { SportEvent } from '@/features/ses';
import { getEventTypeIcon } from './PlayByPlayUtils';
import useConfirmationDialogContext from '@/shared/hooks/useConfirmationDialogContext';
import Tracking from '@/shared/services/tracking';
import { useGameTimeFormatter } from '@/features/games/hooks/useGameTimeFormatter';
import { displayWallClock } from '@/shared/services';

interface EventInClipCreationProps {
  event: SportEvent;
  teamLogoPath: string;
  gameTime: string;
  description: string;
  onEdit?: (newDescription: string) => void;
  onConfirmDelete?: () => Promise<void> | void;
}

const teamLogoStyle: SxProps<Theme> = {
  width: 42,
  height: 42,
  objectFit: 'contain',
  margin: 1,
};

const descriptionTextStyle: SxProps<Theme> = {
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '22px',
};

const timeTextStyle: SxProps<Theme> = {
  fontSize: 14,
  fontWeight: 300,
  lineHeight: '20px',
};

const MIN_NAME_LENGTH = 4;

// One predicate for all three commit paths — Enter, click-away and the commit
// button's disabled state — so they cannot drift apart. The placeholder is
// excluded explicitly: it is long enough to pass the length test but still
// means "not yet named".
const isValidEventName = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length >= MIN_NAME_LENGTH && trimmed !== 'New Custom Event';
};

export const EventInClipCreation: React.FC<EventInClipCreationProps> = ({
  event,
  teamLogoPath,
  gameTime,
  description,
  onEdit,
  onConfirmDelete,
}) => {
  const theme = useTheme();
  const { getActiveTimezone } = useGameTimeFormatter();
  const isCustomEvent = (event as any).payload?.type === 'custom';
  const setConfirmationDialog = useConfirmationDialogContext();

  // A "draft" custom event has no customEventId yet — it lives only in the
  // client until the user commits a name. Treat empty descriptions and the
  // legacy 'New Custom Event' placeholder as not-yet-named for back-compat.
  const isDraftCustomEvent = isCustomEvent && !(event as any).customEventId;
  const isFreshCustomEvent =
    isDraftCustomEvent || (isCustomEvent && description === 'New Custom Event');
  const [isEditing, setIsEditing] = useState(isFreshCustomEvent);
  const [editedValue, setEditedValue] = useState(isFreshCustomEvent ? '' : description);
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Sync local state with the description prop. Fresh custom events open
  // straight into edit mode with an empty value so the user is prompted
  // to name the event instead of having to discover the inline edit.
  useEffect(() => {
    const fresh =
      (isCustomEvent && !(event as any).customEventId) ||
      (isCustomEvent && description === 'New Custom Event');
    if (fresh) {
      setEditedValue('');
      setIsEditing(true);
    } else {
      setEditedValue(description);
    }
  }, [description, isCustomEvent, event]);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus();
      textFieldRef.current.select();
    }
  }, [isEditing]);

  // Format time display with timezone awareness
  const displayTime = useCallback(
    (time: Date | string | undefined) => {
      if (!time) return '';
      let dateObj: Date;

      if (time instanceof Date) {
        dateObj = time;
      } else if (typeof time === 'string') {
        // Try to parse the string as a Date
        dateObj = new Date(time);

        // If parsing fails, return the original string
        if (isNaN(dateObj.getTime())) {
          return time;
        }
      } else {
        return String(time);
      }

      return displayWallClock(dateObj, { timezone: getActiveTimezone() });
    },
    [getActiveTimezone],
  );

  // Handle clicking on description to edit
  const handleDescriptionClick = useCallback(() => {
    if (isCustomEvent && onEdit) {
      // Clear the textbox if it's the default "New Custom Event"
      if (description === 'New Custom Event') {
        setEditedValue('');
      }
      setIsEditing(true);
    }
  }, [isCustomEvent, onEdit, description]);

  // Handle blur - save changes. If the user blurs without committing a real
  // name on a custom event, keep them in edit mode and refocus so the drawer
  // doesn't disappear out from under them (the event would otherwise be
  // auto-deleted on close).
  const handleBlur = useCallback(() => {
    const trimmed = editedValue.trim();
    if (isCustomEvent && !isValidEventName(editedValue)) {
      requestAnimationFrame(() => {
        textFieldRef.current?.focus();
      });
      return;
    }
    setIsEditing(false);
    if (onEdit && editedValue !== description) {
      onEdit(trimmed);
    }
  }, [onEdit, editedValue, description, isCustomEvent]);

  // Handle Enter key - save changes
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        textFieldRef.current?.blur();
      } else if (e.key === 'Escape') {
        setEditedValue(description);
        setIsEditing(false);
      }
    },
    [description],
  );

  const handleDeleteClick = useCallback(() => {
    if (!onConfirmDelete) {
      return;
    }

    if (!setConfirmationDialog) {
      onConfirmDelete();
      return;
    }

    setConfirmationDialog({
      title: 'Delete Custom Event',
      message: 'Are you sure you want to delete this custom event? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonStyle: 'error',
      onConfirm: async () => {
        await onConfirmDelete();
        Tracking.getInstance().track('Delete Custom Event', { category: 'Clips' });
      },
    });
  }, [onConfirmDelete, setConfirmationDialog]);

  return (
    <Stack
      direction="row"
      sx={{
        p: 1,
        mx: 1.5,
        minHeight: '52px',
        alignItems: 'center',
        gap: 2,
        borderRadius: 1,
        bgcolor: theme.palette.cx.bg.elevated,
      }}
    >
      {/* Logo/Icon */}
      {(() => {
        const IconComponent = getEventTypeIcon((event as any).payload?.type);
        if (IconComponent) {
          return (
            <Box
              sx={{
                width: 42,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                m: 1,
              }}
            >
              <IconComponent />
            </Box>
          );
        }
        return <Box component="img" src={teamLogoPath} alt="team" sx={teamLogoStyle} />;
      })()}

      {/* Content */}
      <Stack direction="column" sx={{ flex: 1 }}>
        {/* Description - Editable for custom events */}
        {isEditing ? (
          // Inline-grid auto-width pattern: the hidden replica sets the width,
          // so the dashed underline ends with the text instead of spanning the
          // whole row.
          <Box
            sx={{
              display: 'inline-grid',
              justifySelf: 'start',
              maxWidth: '100%',
              '& > *': { gridArea: '1 / 1' },
            }}
          >
            <Typography
              aria-hidden="true"
              sx={{
                ...descriptionTextStyle,
                height: '22px',
                visibility: 'hidden',
                whiteSpace: 'pre',
              }}
            >
              {editedValue || 'New Custom Event'}
            </Typography>
            <TextField
              inputRef={textFieldRef}
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder="New Custom Event"
              variant="standard"
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    ...descriptionTextStyle,
                    borderBottom: '1px dashed ' + theme.palette.cx.text.tertiary,
                    height: '22px',
                    color: 'rgba(255,255,255)',
                    '& input': { padding: 0 },
                  },
                },
              }}
            />
          </Box>
        ) : (
          <Typography
            onClick={handleDescriptionClick}
            sx={{
              ...descriptionTextStyle,
              color: 'rgba(255,255,255)',
              cursor: isCustomEvent && onEdit ? 'pointer' : 'default',
              '&:hover':
                isCustomEvent && onEdit
                  ? {
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                    }
                  : {},
            }}
          >
            {description}
          </Typography>
        )}

        <Stack direction="row" spacing={1}>
          {/* Time */}
          <Typography sx={{ ...timeTextStyle, color: theme.palette.cx.text.tertiary }}>
            {gameTime}
          </Typography>
          <Typography sx={{ ...timeTextStyle, color: theme.palette.cx.text.tertiary }}>
            |
          </Typography>
          <Typography sx={{ ...timeTextStyle, color: theme.palette.cx.text.tertiary }}>
            {displayTime(event.timestamp)}
          </Typography>
        </Stack>
      </Stack>

      {/* Delete button for custom events */}
      {isCustomEvent && !isEditing && onConfirmDelete && (
        <Stack direction="row" spacing={1}>
          <IconButton
            onClick={handleDescriptionClick}
            size="small"
            sx={{
              width: 28,
              height: 28,
              padding: 0,
              border: `1px solid ${theme.palette.cx.text.secondary}`,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_4780_17733)">
                <path
                  d="M12.3513 3.97373C12.6597 3.66539 12.833 3.24717 12.8331 2.81106C12.8331 2.37495 12.6599 1.95668 12.3516 1.64827C12.0433 1.33985 11.625 1.16656 11.1889 1.1665C10.7528 1.16645 10.3345 1.33964 10.0261 1.64798L2.24097 9.43489C2.10553 9.56993 2.00537 9.7362 1.9493 9.91906L1.17872 12.4577C1.16364 12.5082 1.1625 12.5618 1.17542 12.6128C1.18834 12.6638 1.21484 12.7104 1.2521 12.7476C1.28936 12.7848 1.33599 12.8113 1.38706 12.8241C1.43812 12.8369 1.49171 12.8357 1.54213 12.8206L4.08138 12.0506C4.26406 11.995 4.43032 11.8954 4.56555 11.7606L12.3513 3.97373Z"
                  stroke="#CBCBD2"
                  strokeWidth="0.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.75 2.9165L11.0833 5.24984"
                  stroke="#CBCBD2"
                  strokeWidth="0.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_4780_17733">
                  <rect width="14" height="14" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </IconButton>
          <IconButton
            onClick={handleDeleteClick}
            size="small"
            sx={{
              width: 28,
              height: 28,
              padding: 0,
              border: `1px solid ${theme.palette.error.main}`,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: 'rgba(255, 0, 0, 0.1)',
              },
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.75006 3.28141C8.41017 3.28141 8.07157 3.28685 7.73433 3.29764C7.21123 3.31437 6.79457 3.75293 6.79457 4.29538V4.4709C7.4411 4.42938 8.09314 4.40831 8.75006 4.40831C9.40698 4.40831 10.059 4.42938 10.7056 4.4709V4.29538C10.7056 3.75293 10.2889 3.31437 9.76578 3.29764C9.42855 3.28685 9.08994 3.28141 8.75006 3.28141ZM11.4347 4.52657V4.29538C11.4347 3.37852 10.7246 2.59877 9.7891 2.56884C9.44409 2.55781 9.09772 2.55225 8.75006 2.55225C8.4024 2.55225 8.05603 2.55781 7.71102 2.56884C6.77556 2.59877 6.0654 3.37852 6.0654 4.29538V4.52657C5.44603 4.5814 4.83202 4.65501 4.22397 4.74684C4.00979 4.77918 3.79634 4.81379 3.58367 4.85063C3.38527 4.885 3.25229 5.07369 3.28666 5.27209C3.32103 5.47049 3.50972 5.60346 3.70812 5.5691C3.78703 5.55543 3.86606 5.54207 3.94519 5.52903L4.57556 13.7239C4.64596 14.6391 5.40911 15.3458 6.32702 15.3458H11.1731C12.091 15.3458 12.8542 14.6391 12.9246 13.7239L13.5549 5.52903C13.6341 5.54207 13.7131 5.55543 13.792 5.5691C13.9904 5.60346 14.1791 5.47049 14.2135 5.27209C14.2478 5.07369 14.1149 4.885 13.9165 4.85063C13.7038 4.81379 13.4903 4.77918 13.2762 4.74684C12.6681 4.65501 12.0541 4.5814 11.4347 4.52657ZM12.8321 5.41919C12.2409 5.33688 11.644 5.2722 11.042 5.22568C10.2858 5.16724 9.52144 5.13747 8.75006 5.13747C7.97868 5.13747 7.21433 5.16724 6.45807 5.22568C5.8561 5.2722 5.25924 5.33688 4.66806 5.41919L5.30258 13.668C5.34376 14.2033 5.79013 14.6166 6.32702 14.6166H11.1731C11.71 14.6166 12.1564 14.2033 12.1975 13.668L12.8321 5.41919ZM7.04061 6.72865C7.24181 6.72091 7.41119 6.87775 7.41893 7.07895L7.63309 12.6471C7.64083 12.8483 7.484 13.0177 7.28279 13.0255C7.08159 13.0332 6.91221 12.8764 6.90447 12.6752L6.69031 7.10698C6.68257 6.90577 6.8394 6.73639 7.04061 6.72865ZM10.4595 6.72865C10.6607 6.73639 10.8176 6.90577 10.8098 7.10698L10.5957 12.6752C10.5879 12.8764 10.4185 13.0332 10.2173 13.0255C10.0161 13.0177 9.85929 12.8483 9.86702 12.6471L10.0812 7.07895C10.0889 6.87775 10.2583 6.72091 10.4595 6.72865Z"
                fill="#DBDBE1"
              />
            </svg>
          </IconButton>
        </Stack>
      )}

      {/* Commit button — the click equivalent of pressing Enter, so the
          inline editor has a visible affordance and not only a hidden
          keyboard one. */}
      {isCustomEvent && isEditing && onEdit && (
        <Tooltip
          title={
            isValidEventName(editedValue)
              ? ''
              : `Enter a name of at least ${MIN_NAME_LENGTH} characters`
          }
          arrow
        >
          {/* Mousedown's default is to move focus, which would blur the field
              and let handleBlur commit and unmount this button before its
              click ever landed. Prevented on the span rather than the button
              because a disabled IconButton has pointer-events: none, so the
              event would otherwise reach the row and blur the field anyway. */}
          <span onMouseDown={(e) => e.preventDefault()}>
            <IconButton
              onClick={handleBlur}
              disabled={!isValidEventName(editedValue)}
              size="small"
              aria-label="Save event name"
              sx={{
                width: 28,
                height: 28,
                padding: 0,
                border: `1px solid ${theme.palette.cx.green}`,
                '&:hover': {
                  backgroundColor: 'rgba(18, 183, 106, 0.1)',
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                  border: `1px solid ${theme.palette.cx.border.subtle}`,
                },
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M11.739 3.45098C11.8731 3.24994 11.8187 2.97831 11.6177 2.84428C11.4166 2.71025 11.145 2.76457 11.011 2.96562L6.057 10.3966L2.93436 7.27394C2.7635 7.10308 2.4865 7.10308 2.31564 7.27394C2.14479 7.44479 2.14479 7.7218 2.31564 7.89266L5.81564 11.3927C5.90832 11.4853 6.03768 11.5316 6.16811 11.5187C6.29854 11.5058 6.41632 11.435 6.48902 11.326L11.739 3.45098Z"
                  fill="white"
                />
              </svg>
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};
